import { useState } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Inbox, Home, Car, Hotel } from "lucide-react";
import { BrandMark } from "@/components/vroom/brand-mark";
import { Kbd } from "@/components/vroom/kbd";
import { NumPill } from "@/components/vroom/num-pill";
import { Chip } from "@/components/vroom/chip";
import { StatusBadge } from "@/components/vroom/status-badge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SearchBar } from "@/components/vroom/search-bar";
import { ScopeNav } from "@/components/vroom/scope-nav";
import { SegmentedControl } from "@/components/vroom/segmented-control";
import { AppearanceSection } from "@/components/appearance-section";
import { LanguagePicker } from "@/components/vroom/language-picker";
import { DateRangePicker } from "@/components/vroom/date-range-picker";
import { TimePicker } from "@/components/vroom/time-picker";
import { TabBar } from "@/components/vroom/tab-bar";
import { FilterChipBar } from "@/components/vroom/filter-chip-bar";
import { RolePill } from "@/components/vroom/role-pill";
import { SidebarNavItem } from "@/components/vroom/sidebar-nav-item";
import { BottomNavItem } from "@/components/vroom/bottom-nav-item";
import { NavSectionLabel } from "@/components/vroom/nav-section-label";
import { VroomBarChart } from "@/components/wrapped/charts/vroom-bar-chart";
import { VroomLineChart } from "@/components/wrapped/charts/vroom-line-chart";
import { VroomDonut } from "@/components/wrapped/charts/vroom-donut";
import { VroomSparkline } from "@/components/wrapped/charts/vroom-sparkline";
import { DataTable, TableCellId, TableCellNumber } from "@/components/wrapped/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { AdminTopbar } from "@/components/vroom/admin-topbar";
import { MobileTopbar } from "@/components/vroom/mobile-topbar";
import { BackButtonHeader } from "@/components/vroom/back-button-header";
import {
  IndexPageHeader,
  IndexPageHeaderTitle,
  IndexPageHeaderTabs,
  IndexPageHeaderToolbar,
} from "@/components/vroom/index-page-header";
import { Sidebar } from "@/components/vroom/sidebar";
import { InlineDrawer } from "@/components/vroom/inline-drawer";
import { ResponsiveDialog, ResponsiveDialogFooter } from "@/components/responsive-dialog";
import { ProfileDialog, type ProfileSection } from "@/components/profile-dialog/profile-dialog";
import { EmptyState } from "@/components/vroom/empty-state";
import { ConfirmDialog } from "@/components/vroom/confirm-dialog";
import { CommandPalette } from "@/components/vroom/command-palette";
import { TranslationTree } from "@/components/vroom/translation-tree";
import { TranslationLeafEditor } from "@/components/vroom/translation-leaf-editor";
import { FilePlus, Coffee } from "lucide-react";
import { AdminShell } from "@/components/layout/admin-shell";
import { DetailScreenLayout } from "@/components/layout/detail-screen-layout";
import { MobileAdminShell } from "@/components/layout/mobile-admin-shell";

export const Route = createFileRoute("/_authed/admin/styleguide")({
  beforeLoad: ({ context }) => {
    if (import.meta.env.MODE === "production") {
      throw redirect({ to: "/dashboard" });
    }
    const user = context.user as { role?: string } | undefined;
    if (user?.role !== "super_admin") {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: StyleguidePage,
});

const COLOR_TOKENS = [
  "--vroom-red",
  "--vroom-red-soft",
  "--vroom-red-edge",
  "--p-50",
  "--p-100",
  "--p-200",
  "--p-300",
  "--p-400",
  "--p-500",
  "--p-600",
  "--p-700",
  "--p-800",
  "--ink-900",
  "--ink-700",
  "--ink-500",
  "--ink-400",
  "--ink-300",
  "--surface",
  "--surface-1",
  "--surface-2",
  "--line",
  "--line-strong",
  "--st-todo-bg",
  "--st-todo-fg",
  "--st-ok-bg",
  "--st-ok-fg",
  "--st-rej-bg",
  "--st-rej-fg",
  "--st-exp-bg",
  "--st-exp-fg",
  "--cat-1",
  "--cat-2",
  "--cat-3",
  "--cat-4",
  "--cat-5",
  "--cat-6",
  "--cat-7",
  "--avatar-1",
  "--avatar-2",
  "--avatar-3",
  "--avatar-4",
  "--avatar-5",
  "--avatar-6",
  "--avatar-7",
  "--avatar-8",
] as const;

const RADII = ["--r-1", "--r-2", "--r-3", "--r-4", "--r-5", "--r-6", "--r-full"] as const;

const SPACES = [
  "--space-0",
  "--space-1",
  "--space-2",
  "--space-3",
  "--space-4",
  "--space-5",
  "--space-6",
  "--space-7",
  "--space-8",
  "--space-9",
  "--space-10",
] as const;

function Swatch({ token }: { token: string }) {
  return (
    <div className="flex items-center gap-3 rounded-4 border border-line p-3">
      <div
        className="size-10 rounded-3 border border-line-strong"
        style={{ background: `var(${token})` }}
      />
      <div className="font-mono text-xs text-ink-700">{token}</div>
    </div>
  );
}

function RadiusSample({ token }: { token: string }) {
  return (
    <div className="flex items-center gap-3 rounded-4 border border-line p-3">
      <div
        className="size-12 border border-line-strong bg-p-100"
        style={{ borderRadius: `var(${token})` }}
      />
      <div className="font-mono text-xs text-ink-700">{token}</div>
    </div>
  );
}

function SpaceSample({ token }: { token: string }) {
  return (
    <div className="flex items-center gap-3 rounded-4 border border-line p-3">
      <div className="h-3 bg-p-600" style={{ width: `var(${token})` }} />
      <div className="font-mono text-xs text-ink-700">{token}</div>
    </div>
  );
}

function StyleguidePage() {
  const { t } = useTranslation();
  return (
    <main className="mx-auto max-w-6xl px-6 py-10">
      <h1 className="text-2xl font-semibold text-ink-900">
        {t("admin.styleguide.demo.page-title")}
      </h1>
      <p className="mt-2 text-sm text-ink-500">{t("admin.styleguide.demo.page-subtitle")}</p>
      <nav className="mt-6 flex flex-wrap gap-3 text-sm">
        <a href="#tokens" className="text-p-700 underline">
          Tokens
        </a>
        <a href="#atoms" className="text-p-700 underline">
          Atoms
        </a>
        <a href="#molecules" className="text-p-700 underline">
          Molecules
        </a>
        <a href="#wrapped" className="text-p-700 underline">
          Wrapped
        </a>
        <a href="#organisms" className="text-p-700 underline">
          Organisms
        </a>
        <a href="#layouts" className="text-p-700 underline">
          Layouts
        </a>
      </nav>

      <section id="tokens" className="mt-10">
        <h2 className="text-lg font-semibold text-ink-900">Tokens</h2>

        <h3 className="mt-6 text-sm font-semibold text-ink-700">Colors</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {COLOR_TOKENS.map((t) => (
            <Swatch key={t} token={t} />
          ))}
        </div>

        <h3 className="mt-8 text-sm font-semibold text-ink-700">Radii</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {RADII.map((t) => (
            <RadiusSample key={t} token={t} />
          ))}
        </div>

        <h3 className="mt-8 text-sm font-semibold text-ink-700">Spacing</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {SPACES.map((t) => (
            <SpaceSample key={t} token={t} />
          ))}
        </div>

        <h3 className="mt-8 text-sm font-semibold text-ink-700">Typography</h3>
        <div className="mt-3 space-y-2">
          <div
            style={{ fontFamily: "var(--font-display)" }}
            className="text-[22px] font-semibold tracking-tight"
          >
            Page title — Plex Condensed 22/600
          </div>
          <div className="text-[15px] font-semibold">Section title — Sans 15/600</div>
          <div className="text-[13px]">
            Body — Sans 13/400 — the quick brown fox jumps over the lazy dog
          </div>
          <div className="text-[12.5px]">Body small — Sans 12.5/400</div>
          <div className="text-[12px] text-ink-500">Meta — Sans 12/400 ink-500</div>
          <div style={{ fontFamily: "var(--font-mono)" }} className="text-[13px] tabular-nums">
            Number — Mono 13/500 tabular 1234567890
          </div>
          <div
            style={{ fontFamily: "var(--font-mono)" }}
            className="text-[12px] tracking-tight text-ink-700"
          >
            ID — Mono 12/400 <span className="text-ink-400">WO-</span>1042
          </div>
          <div
            style={{ fontFamily: "var(--font-mono)", letterSpacing: ".12em" }}
            className="text-[9.5px] uppercase text-ink-500"
          >
            Section label — Mono 9.5/500
          </div>
        </div>
      </section>

      <section id="atoms" className="mt-10 space-y-8">
        <h2 className="text-lg font-semibold text-ink-900">Atoms</h2>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">BrandMark</h3>
          <div className="mt-2 flex items-center gap-3">
            <BrandMark size={20} />
            <BrandMark size={26} />
            <BrandMark size={40} />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">Button</h3>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button variant="primary">
              {t("admin.styleguide.demo.buttons.approve.label")} <NumPill inverted>12</NumPill>{" "}
              <Kbd inverted>G</Kbd>
            </Button>
            <Button variant="default">
              {t("common.buttons.export.label")} <Kbd>E</Kbd>
            </Button>
            <Button variant="ghost">{t("common.buttons.cancel.label")}</Button>
            <Button variant="danger-soft">
              {t("admin.styleguide.demo.buttons.reject.label")} <Kbd>X</Kbd>
            </Button>
            <Button variant="primary" disabled>
              Disabled
            </Button>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">Badge</h3>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Badge>214</Badge>
            <Badge variant="count">12</Badge>
            <Badge variant="soft">{t("admin.styleguide.demo.badge.soft-todo")}</Badge>
            <Badge variant="role">{t("common.roles.approver.label")}</Badge>
            <Badge variant="status-todo">{t("common.status.todo.label")}</Badge>
            <Badge variant="status-ok">{t("common.status.ok.label")}</Badge>
            <Badge variant="status-rej">{t("common.status.rej.label")}</Badge>
            <Badge variant="status-exp">{t("common.status.exp.label")}</Badge>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">Input</h3>
          <div className="mt-2 grid max-w-md gap-3">
            <Input placeholder={t("admin.styleguide.demo.input.email-placeholder")} />
            <Input placeholder={t("admin.styleguide.demo.input.error-placeholder")} aria-invalid />
            <Input placeholder={t("admin.styleguide.demo.input.disabled-placeholder")} disabled />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">Avatar</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {(
              [
                ["Brian", "Diephuis"],
                ["Anna", "Smith"],
                ["Mark", "Vroom"],
                ["Sara", "Janssen"],
              ] as const
            ).map(([f, l]) => (
              <Avatar key={f}>
                <AvatarFallback initials={(f.charAt(0) + l.charAt(0)).toUpperCase()} />
              </Avatar>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">Chip</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Chip>{t("admin.styleguide.demo.chip.cost-center")}</Chip>
            <Chip variant="active">{t("common.status.todo.label")}</Chip>
            <Chip variant="dashed-plus">{t("common.filter-chip.add.label")}</Chip>
            <Chip removable onRemove={() => {}}>
              {t("admin.styleguide.demo.chip.worker-anna")}
            </Chip>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">StatusBadge</h3>
          <div className="mt-2 flex flex-wrap items-center gap-4">
            <StatusBadge status="todo" />
            <StatusBadge status="ok" />
            <StatusBadge status="rej" />
            <StatusBadge status="exp" />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">Kbd</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Kbd>G</Kbd>
            <Kbd>X</Kbd>
            <Kbd>⌘ K</Kbd>
            <Kbd>Esc</Kbd>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-ink-700">NumPill</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <NumPill>12</NumPill>
            <NumPill>214</NumPill>
            <NumPill inverted>12</NumPill>
          </div>
        </div>
      </section>
      <MoleculesPanel />
      <WrappedPanel />
      <OrganismsPanel />
      <LayoutsPanel />
    </main>
  );
}

function MoleculesPanel() {
  const { t } = useTranslation();
  const [view, setView] = useState<"per-werkorder" | "per-werknemer">("per-werkorder");
  const [tab, setTab] = useState("todo");
  const [time, setTime] = useState<string | undefined>("08:00");
  return (
    <section id="molecules" className="mt-10 space-y-8">
      <h2 className="text-lg font-semibold text-ink-900">Molecules</h2>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">SearchBar</h3>
        <div className="mt-2 max-w-md">
          <SearchBar />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">ScopeNav</h3>
        <div className="mt-2">
          <ScopeNav onPrev={() => {}} onNext={() => {}} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">SegmentedControl</h3>
        <div className="mt-2">
          <SegmentedControl
            ariaLabel={t("admin.styleguide.demo.segmented.aria-label")}
            value={view}
            onValueChange={setView}
            options={[
              {
                value: "per-werkorder",
                label: t("admin.styleguide.demo.segmented.per-work-order"),
              },
              {
                value: "per-werknemer",
                label: t("admin.styleguide.demo.segmented.per-worker"),
              },
            ]}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">AppearanceSection</h3>
        <div className="mt-2 max-w-sm">
          <AppearanceSection />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">LanguagePicker</h3>
        <div className="mt-2">
          <LanguagePicker
            ariaLabel={t("admin.styleguide.demo.language-picker.aria-label")}
            value="nl"
            onValueChange={() => {}}
            languages={["nl", "en"]}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">DateRangePicker + TimePicker</h3>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <DateRangePicker value={{}} onValueChange={() => {}} />
          <TimePicker value={time} onValueChange={setTime} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">TabBar (attention coding)</h3>
        <div className="mt-2">
          <TabBar
            ariaLabel={t("admin.styleguide.demo.tabs.aria-label")}
            value={tab}
            onValueChange={setTab}
            tabs={[
              {
                value: "todo",
                label: t("common.status.todo.label"),
                count: 12,
                attention: true,
              },
              { value: "ok", label: t("common.status.ok.label"), count: 8 },
              { value: "rej", label: t("common.status.rej.label"), count: 2 },
              { value: "exp", label: t("common.status.exp.label"), count: 214 },
            ]}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">FilterChipBar</h3>
        <div className="mt-2">
          <FilterChipBar
            chips={[
              { id: "kp", label: t("admin.styleguide.demo.chip.cost-center") },
              { id: "wo", label: t("admin.styleguide.demo.chip.work-order") },
            ]}
            onRemove={() => {}}
            onAdd={() => {}}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">RolePill</h3>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <RolePill role="worker" />
          <RolePill role="approver" />
          <RolePill role="super_admin" />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">Sidebar nav</h3>
        <div className="mt-2 w-56 rounded-4 border border-line bg-surface-1 p-2">
          <NavSectionLabel>{t("common.nav.work.label")}</NavSectionLabel>
          <SidebarNavItem
            to="/dashboard"
            icon={<Home size={14} />}
            label={t("admin.styleguide.demo.sidebar-nav.hours-overview")}
            count={12}
            active
          />
          <SidebarNavItem
            to="/dashboard"
            icon={<Inbox size={14} />}
            label={t("admin.styleguide.demo.sidebar-nav.inbox")}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">Bottom nav</h3>
        <div className="mt-2 flex h-16 w-80 items-stretch rounded-4 border border-line bg-surface">
          <BottomNavItem
            to="/dashboard"
            icon={<Home size={20} />}
            label={t("admin.styleguide.demo.bottom-nav.home")}
            active
          />
          <BottomNavItem
            to="/dashboard"
            icon={<Inbox size={20} />}
            label={t("admin.styleguide.demo.bottom-nav.inbox")}
            badge={3}
          />
          <BottomNavItem
            to="/dashboard"
            icon={<Car size={20} />}
            label={t("admin.styleguide.demo.bottom-nav.rides")}
          />
          <BottomNavItem
            to="/dashboard"
            icon={<Hotel size={20} />}
            label={t("admin.styleguide.demo.bottom-nav.lodging")}
          />
        </div>
      </div>
    </section>
  );
}

const BAR_DATA = [
  { week: "W18", regulier: 38, "150%": 4, "200%": 0 },
  { week: "W19", regulier: 40, "150%": 6, "200%": 2 },
  { week: "W20", regulier: 36, "150%": 8, "200%": 0 },
];
const LINE_DATA = [
  { week: "W18", uren: 42 },
  { week: "W19", uren: 48 },
  { week: "W20", uren: 44 },
];

function WrappedPanel() {
  const { t } = useTranslation();
  const donutData = [
    { name: t("common.status.todo.label"), value: 12, status: "todo" as const },
    { name: t("common.status.ok.label"), value: 8, status: "ok" as const },
    { name: t("common.status.rej.label"), value: 2, status: "rej" as const },
    { name: t("common.status.exp.label"), value: 214, status: "exp" as const },
  ];
  const tableData = [
    { code: "4200", name: t("admin.styleguide.demo.table.row-pile-driving"), hours: 32.5 },
    { code: "4201", name: t("admin.styleguide.demo.table.row-foundations"), hours: 40 },
  ];
  const tableCols: ColumnDef<(typeof tableData)[number], unknown>[] = [
    {
      accessorKey: "code",
      header: t("admin.styleguide.demo.table.header-code"),
      cell: (i) => <TableCellId prefix="KP-" value={i.getValue<string>()} />,
    },
    { accessorKey: "name", header: t("admin.styleguide.demo.table.header-name") },
    {
      accessorKey: "hours",
      header: t("admin.styleguide.demo.table.header-hours"),
      cell: (i) => <TableCellNumber>{i.getValue<number>()}</TableCellNumber>,
    },
  ];
  return (
    <section id="wrapped" className="mt-10 space-y-8">
      <h2 className="text-lg font-semibold text-ink-900">Wrapped (charts &amp; tables)</h2>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="text-sm font-semibold text-ink-700">VroomBarChart</h3>
          <VroomBarChart
            data={BAR_DATA}
            xKey="week"
            series={[
              { key: "regulier", label: "100%" },
              { key: "150%", label: "150%" },
              { key: "200%", label: "200%" },
            ]}
          />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-ink-700">VroomLineChart</h3>
          <VroomLineChart
            data={LINE_DATA}
            xKey="week"
            series={[{ key: "uren", label: t("admin.styleguide.demo.chart.series-hours") }]}
          />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-ink-700">VroomDonut</h3>
          <VroomDonut data={donutData} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-ink-700">VroomSparkline</h3>
          <VroomSparkline data={[42, 48, 44, 46, 50, 47, 49].map((v) => ({ value: v }))} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">DataTable</h3>
        <div className="mt-2 max-w-2xl">
          <DataTable data={tableData} columns={tableCols} />
        </div>
      </div>
    </section>
  );
}

const SAMPLE_USER = {
  firstName: "Brian",
  lastName: "Diephuis",
  email: "briandiephuis@hoppinger.com",
  erpId: "5421",
  role: "super_admin" as const,
};

function OrganismsPanel() {
  const { t } = useTranslation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSection, setProfileSection] = useState<ProfileSection>("account");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [leafValue, setLeafValue] = useState(
    t("admin.styleguide.demo.translation.preview-invoice-upload"),
  );
  const sampleTree = [
    {
      name: "worker",
      counts: { missing: 12, total: 456 },
      branches: [
        {
          name: "kost",
          counts: { missing: 12 },
          leaves: [
            {
              key: "title",
              preview: t("admin.styleguide.demo.translation.preview-missing"),
              status: "missing" as const,
            },
            {
              key: "cta",
              preview: t("admin.styleguide.demo.translation.preview-invoice-upload"),
              status: "ok" as const,
            },
          ],
        },
      ],
    },
  ];
  const commandEntries = [
    {
      id: "g-dashboard",
      label: t("admin.styleguide.demo.cmd-palette.go-to-hours-overview"),
      group: t("admin.styleguide.demo.cmd-palette.group-navigation"),
      onSelect: () => {},
    },
    {
      id: "g-inbox",
      label: t("admin.styleguide.demo.cmd-palette.go-to-inbox"),
      group: t("admin.styleguide.demo.cmd-palette.group-navigation"),
      onSelect: () => {},
    },
    {
      id: "export",
      label: t("admin.styleguide.demo.cmd-palette.export-to-afas"),
      group: t("admin.styleguide.demo.cmd-palette.group-actions"),
      onSelect: () => {},
    },
  ];
  return (
    <section id="organisms" className="mt-10 space-y-8">
      <h2 className="text-lg font-semibold text-ink-900">Organisms</h2>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">AdminTopbar</h3>
        <div className="mt-2 overflow-hidden rounded-5 border border-line">
          <AdminTopbar
            breadcrumb={<span>{t("admin.styleguide.demo.breadcrumb")}</span>}
            user={SAMPLE_USER}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">MobileTopbar (worker / admin)</h3>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <div className="overflow-hidden rounded-5 border border-line">
            <MobileTopbar variant="worker" user={SAMPLE_USER} />
          </div>
          <div className="overflow-hidden rounded-5 border border-line">
            <MobileTopbar variant="admin" user={SAMPLE_USER} />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">BackButtonHeader</h3>
        <div className="mt-2 overflow-hidden rounded-5 border border-line">
          <BackButtonHeader
            title={t("admin.styleguide.demo.back-button-title")}
            onBack={() => {}}
            rightSlot="W20 · 2026"
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">IndexPageHeader</h3>
        <div className="mt-2 overflow-hidden rounded-5 border border-line">
          <IndexPageHeader>
            <IndexPageHeaderTitle
              title={t("admin.styleguide.demo.index-header.title")}
              kicker="W20 · 2026"
              onPrev={() => {}}
              onNext={() => {}}
            />
            <IndexPageHeaderTabs>
              <div className="py-2 text-[12.5px] text-ink-500">Tabs slot (see TabBar)</div>
            </IndexPageHeaderTabs>
            <IndexPageHeaderToolbar>
              <span className="text-[12.5px] text-ink-700">
                {t("admin.styleguide.demo.index-header.filters-slot")}
              </span>
              <span className="text-[12.5px] text-ink-700">
                {t("admin.styleguide.demo.index-header.actions-slot")}
              </span>
            </IndexPageHeaderToolbar>
          </IndexPageHeader>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">Sidebar (admin)</h3>
        <div className="mt-2">
          <Sidebar
            sections={[
              {
                label: t("common.nav.work.label"),
                items: <div className="px-2 text-[12px] text-ink-500">items</div>,
              },
              {
                label: t("common.nav.admin.label"),
                items: <div className="px-2 text-[12px] text-ink-500">items</div>,
              },
            ]}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">InlineDrawer</h3>
        <div className="mt-2 overflow-hidden rounded-5 border border-line">
          <InlineDrawer open>
            <div className="text-[12.5px] text-ink-700">
              {t("admin.styleguide.demo.inline-drawer.selection")}
            </div>
          </InlineDrawer>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">EmptyState</h3>
        <div className="mt-2 grid gap-3 md:grid-cols-3">
          <div className="rounded-5 border border-line bg-surface">
            <EmptyState
              variant="onboarding"
              size="small"
              icon={<FilePlus size={18} />}
              title={t("admin.styleguide.demo.empty-onboarding.title")}
              description={t("admin.styleguide.demo.empty-onboarding.description")}
            />
          </div>
          <div className="rounded-5 border border-line bg-surface">
            <EmptyState
              variant="filtered"
              size="medium"
              icon={<Coffee size={20} />}
              title={t("admin.styleguide.demo.empty-filtered.title")}
              description={t("admin.styleguide.demo.empty-filtered.description")}
            />
          </div>
          <div className="rounded-5 border border-line bg-surface">
            <EmptyState
              variant="healthy"
              size="large"
              icon={<Coffee size={26} />}
              title={t("admin.styleguide.demo.empty-healthy.title")}
              description={t("admin.styleguide.demo.empty-healthy.description")}
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">
          {t("admin.styleguide.demo.overlays.heading")}
        </h3>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button variant="default" onClick={() => setProfileOpen(true)}>
            ProfileDialog
          </Button>
          <Button variant="default" onClick={() => setDialogOpen(true)}>
            ResponsiveDialog
          </Button>
          <Button variant="default" onClick={() => setConfirmOpen(true)}>
            ConfirmDialog
          </Button>
          <Button variant="default" onClick={() => setPaletteOpen(true)}>
            CommandPalette
          </Button>
        </div>
        <ProfileDialog
          open={profileOpen}
          onOpenChange={setProfileOpen}
          activeSection={profileSection}
          onSectionChange={setProfileSection}
          user={SAMPLE_USER}
          onSignOut={() => setProfileOpen(false)}
        />
        <ResponsiveDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          title={t("admin.styleguide.demo.dialog.title")}
          description={t("admin.styleguide.demo.dialog.description")}
        >
          <p className="text-[13px] text-ink-700">{t("admin.styleguide.demo.dialog.body")}</p>
          <ResponsiveDialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              {t("common.buttons.cancel.label")}
            </Button>
            <Button variant="primary" onClick={() => setDialogOpen(false)}>
              {t("common.buttons.save.label")}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialog>
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t("common.dialogs.discard-changes.title")}
          description={t("common.dialogs.discard-changes.description")}
          destructiveLabel={t("common.dialogs.discard-changes.confirm.label")}
          onDestructive={() => setConfirmOpen(false)}
          safeLabel={t("common.dialogs.discard-changes.cancel.label")}
          onSafe={() => setConfirmOpen(false)}
        />
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} entries={commandEntries} />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">TranslationTree</h3>
        <div className="mt-2 max-w-2xl">
          <TranslationTree branches={sampleTree} />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">TranslationLeafEditor</h3>
        <div className="mt-2 max-w-3xl">
          <TranslationLeafEditor
            path={["worker", "kost", "deferred-banner", "cta"]}
            reference={{
              language: "nl",
              value: t("admin.styleguide.demo.translation.preview-invoice-upload"),
            }}
            value={leafValue}
            onChange={setLeafValue}
            onCancel={() => {}}
            onSave={() => {}}
          />
        </div>
      </div>
    </section>
  );
}

function LayoutsPanel() {
  const { t } = useTranslation();
  return (
    <section id="layouts" className="mt-10 space-y-8">
      <h2 className="text-lg font-semibold text-ink-900">Layouts</h2>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">AdminShell</h3>
        <div className="relative mt-2 h-[480px] overflow-hidden rounded-5 border border-line">
          <AdminShell
            user={SAMPLE_USER}
            section="admin"
            sidebarSections={[
              {
                label: t("common.nav.work.label"),
                items: <div className="px-2 text-[12px] text-ink-500">items</div>,
              },
            ]}
            breadcrumb={<span>{t("admin.styleguide.demo.breadcrumb")}</span>}
          >
            <div className="px-6 py-4 text-[13px] text-ink-700">
              {t("admin.styleguide.demo.admin-shell.content")}
            </div>
          </AdminShell>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">DetailScreenLayout</h3>
        <div className="relative mt-2 h-[480px] overflow-hidden rounded-5 border border-line">
          <DetailScreenLayout
            title={t("admin.styleguide.demo.detail-screen.title")}
            onBack={() => {}}
            rightSlot="W20 · 2026"
            footer={
              <>
                <Button variant="ghost">{t("common.buttons.cancel.label")}</Button>
                <Button variant="primary">{t("common.buttons.save.label")}</Button>
              </>
            }
          >
            <div className="text-[13px] text-ink-700">
              {t("admin.styleguide.demo.detail-screen.content")}
            </div>
          </DetailScreenLayout>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-700">MobileAdminShell</h3>
        <div className="relative mt-2 mx-auto h-[600px] w-[360px] overflow-hidden rounded-5 border border-line">
          <MobileAdminShell
            user={SAMPLE_USER}
            bottomNavItems={
              <>
                <BottomNavItem
                  to="/dashboard"
                  icon={<Home size={20} />}
                  label={t("admin.styleguide.demo.bottom-nav.work")}
                  active
                />
                <BottomNavItem
                  to="/dashboard"
                  icon={<Inbox size={20} />}
                  label={t("admin.styleguide.demo.bottom-nav.inbox")}
                />
                <BottomNavItem
                  to="/dashboard"
                  icon={<Car size={20} />}
                  label={t("admin.styleguide.demo.bottom-nav.admin")}
                />
              </>
            }
          >
            <div className="p-4 text-[13px] text-ink-700">
              {t("admin.styleguide.demo.mobile-admin.content")}
            </div>
          </MobileAdminShell>
        </div>
      </div>
    </section>
  );
}
